import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { AnalyticsHooks, TabbedTable } from '@frontend/analytics-shared';
import { processExportableData } from '@frontend/file-download-helper';
import { useTranslation } from '@frontend/i18n';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { emptyStateGraphics, TableColumnConfig } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../../hooks/use-show-bulk-message-button';
import { trackingIds } from '../../../tracking-ids';
import { drillDownTableSize } from '../../../views/common-styles';
import { InlineStats } from '../../inline-stats';
import { BulkMessageButton } from '../bulk-message-button';
import { CancellationsMetric } from '../cancellations-metric';
import { deriveExportValue } from '../helpers/patients';
import { useSubViewTableColConfig } from '../helpers/subview-table-col-config';
import { useGetCancellationsMetric } from '../hooks/use-get-cancellations-metric';
import { usePatientsLastContacted } from '../hooks/use-patients-last-contacted';
import { usePracticeAnalyticsShallowStore } from '../hooks/use-practice-analytics-store';
import { SmallModePatientsList } from '../small-mode-patients-list';

type Tab = 'all' | 'hygiene' | 'restorative';

type CategorizedPatients = {
  all: PracticeAnalyticsTypes.PatientInfo[];
  hygiene: PracticeAnalyticsTypes.PatientInfo[];
  restorative: PracticeAnalyticsTypes.PatientInfo[];
};

export const CancellationsSubView: FC<React.PropsWithChildren<PracticeAnalyticsTypes.MetricSubViewProps>> = ({
  exportEnabled,
  onPatientsLoad,
}) => {
  const { t } = useTranslation('analytics');
  const { isSmallMode } = useIsSmallMode();
  const { filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const isBulkMessagingEnabled = useShowBulkMessageButton();
  const { aggregatedCancellationsData, isLoading } = useGetCancellationsMetric(true);

  const [categorizedData, setCategorizedData] = useState<CategorizedPatients>({
    all: [],
    hygiene: [],
    restorative: [],
  });

  const patientIds = useMemo(
    () => aggregatedCancellationsData?.missedAppointments?.details?.patients?.map(({ id }) => id) || [],
    [aggregatedCancellationsData]
  );

  const { lastContactedDates, isLoading: isLoadingLastContacted } = usePatientsLastContacted({ patientIds });

  useEffect(() => {
    const patients = aggregatedCancellationsData?.missedAppointments?.details?.patients || [];

    setCategorizedData(
      patients.reduce(
        (final, patient) => {
          let value = { ...final, all: [...final.all, patient] };
          if (patient.cancelledAppointmentType === 'hygiene') {
            value = { ...value, hygiene: [...value.hygiene, patient] };
          }
          if (patient.cancelledAppointmentType === 'restorative') {
            value = { ...value, restorative: [...value.restorative, patient] };
          }
          return value;
        },
        { all: [], hygiene: [], restorative: [] } as CategorizedPatients
      )
    );
    onPatientsLoad?.(patients);
  }, [aggregatedCancellationsData, onPatientsLoad]);

  const processExportableDataHandler = useCallback(
    (data: PracticeAnalyticsTypes.PatientInfo[]) =>
      // Translation is not needed as this is for export purpose
      processExportableData({
        columns: [
          'Patient Name',
          ...((filters.locations?.length || 0) > 1 ? ['Location Name'] : []),
          'Phone Number',
          'Email',
          'Cancelled Appt',
          'Last Contacted',
          '$ Value',
        ],
        data,
        deriveExportValue: (params) =>
          deriveExportValue({
            ...params,
            lastContactedDates,
            locationNames,
          }),
        sortColumn: 'productionAmount',
      }),
    [filters.locations, lastContactedDates, locationNames]
  );

  const tabs = useMemo(
    () => ({
      all: {
        data: categorizedData.all,
        label: t('All'),
        trackingId: trackingIds.practiceAnalytics.cancellationsAllTab,
      },
      restorative: {
        data: categorizedData.restorative,
        label: t('Restorative'),
        trackingId: trackingIds.practiceAnalytics.cancellationsRestorativeTab,
      },
      hygiene: {
        data: categorizedData.hygiene,
        label: t('Hygiene'),
        trackingId: trackingIds.practiceAnalytics.cancellationsHygieneTab,
      },
    }),
    [categorizedData.all, categorizedData.hygiene, categorizedData.restorative, t]
  );

  const smallModeTableProps: PracticeAnalyticsTypes.SmallModePatientsListProps<Tab> = {
    exportFileName: 'Cancelled Patients', // Translation not needed
    initialTab: 'all',
    isBulkMessagingEnabled,
    isDemoAccount,
    isExportEnabled: exportEnabled,
    isLoadingLastContacted,
    isLoadingPatients: isLoading,
    processExportableData: processExportableDataHandler,
    tabs,
  };

  const largeModeTableProps: PracticeAnalyticsTypes.LargeModeTableProps<Tab> = {
    ...smallModeTableProps,
    lastContactedDates,
    locationNames,
    multiLocations: (filters.locations?.length || 0) > 1,
  };

  return (
    <>
      <InlineStats
        data={
          isSmallMode
            ? [
                {
                  key: 'cancelled',
                  name: t('Cancelled'),
                  value: formatters.value(aggregatedCancellationsData?.missedAppointments?.totals?.cancelled || 0),
                },
                {
                  key: 'unscheduled',
                  name: t('Unscheduled'),
                  value: formatters.value(
                    aggregatedCancellationsData?.missedAppointments?.totals?.cancelledUnscheduled || 0
                  ),
                },
              ]
            : [
                {
                  key: 'cancelledPatients',
                  name: t('Cancelled Patients'),
                  value: formatters.value(aggregatedCancellationsData?.missedAppointments?.totals?.cancelled || 0),
                },
                {
                  key: 'remainingUnscheduled',
                  name: t('Remaining Unscheduled'),
                  value: formatters.value(
                    aggregatedCancellationsData?.missedAppointments?.totals?.cancelledUnscheduled || 0
                  ),
                },
                {
                  key: 'unscheduledOpportunity',
                  name: t('Unscheduled Opportunity'),
                  value: formatters.currency(
                    Math.round(aggregatedCancellationsData?.missedAppointments?.totals?.unscheduledOpportunity || 0)
                  ),
                },
              ]
        }
        isDemoAccount={showDemoChipAndBanner}
        isLoading={isLoading}
      />
      <CancellationsMetric clickNoop isDrillDownPage />
      {isSmallMode ? (
        <SmallModePatientsList<Tab> {...smallModeTableProps} trackingIdBase='cancellations-small-mode' />
      ) : (
        <LargeModeTable {...largeModeTableProps} />
      )}
    </>
  );
};

const LargeModeTable = ({
  tabs,
  isBulkMessagingEnabled,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  processExportableData,
  ...rest
}: PracticeAnalyticsTypes.LargeModeTableProps<Tab>) => {
  const { t } = useTranslation('analytics');

  const middleColumns: TableColumnConfig<PracticeAnalyticsTypes.PatientInfo>[] = [
    {
      Header: t('Cancelled Appt'),
      headerLabel: t('Cancelled Appt'),
      accessor: ({ cancelledAppointmentDate }) => formatters.date(cancelledAppointmentDate),
      id: 'cancelledAppointmentDate',
      sortType: 'date',
      sortInverted: true,
      width: 200,
    },
  ];

  const tabsCommonConfig = {
    colConfig: useSubViewTableColConfig({
      actionsTrackingIdBase: 'cancelled-patient',
      middleColumns,
      patientsLastContacted: {
        isLoading: isLoadingLastContacted,
        lastContactedDates: rest.lastContactedDates,
      },
    }),
    emptyStateConfig: 'users' as keyof typeof emptyStateGraphics,
    tableInstanceId: 'cancellationsPracticeSubView',
  };

  return (
    <TabbedTable<PracticeAnalyticsTypes.PatientInfo>
      {...rest}
      actions={BulkMessageButton}
      autorizedToExport={PracticeAnalyticsApi.auditPracticeAnalyticsExport}
      isExportReady={!isLoadingPatients && !isLoadingLastContacted}
      isLoading={isLoadingPatients}
      isSelectable={isBulkMessagingEnabled}
      processExportableData={processExportableData}
      showExportIcon={isExportEnabled}
      tabs={{
        all: {
          ...tabsCommonConfig,
          ...tabs.all,
        },
        restorative: {
          ...tabsCommonConfig,
          ...tabs.restorative,
        },
        hygiene: {
          ...tabsCommonConfig,
          ...tabs.hygiene,
        },
      }}
      trackingIdBase='cancelled-patients'
      wrapperStyle={drillDownTableSize}
    />
  );
};
